import { useState, useEffect } from "react";

// Types
type Currency = "EUR" | "USD";

interface Location {
  country: string;
  countryCode: string;
  city?: string;
  region?: string;
}

interface UseCurrencyReturn {
  currency: Currency;
  location: Location | null;
  loading: boolean;
  error: string | null;
  formatPrice: (amount: number) => string;
  refetch: () => Promise<void>;
}

// Custom hook for currency detection
export const useCurrency = (): UseCurrencyReturn => {
  const [currency, setCurrency] = useState<Currency>("USD");
  const [location, setLocation] = useState<Location | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // List of European countries that use EUR
  const euroCountries: string[] = [
    "AT",
    "BE",
    "HR",
    "CY",
    "EE",
    "FI",
    "FR",
    "DE",
    "GR",
    "IE",
    "IT",
    "LV",
    "LT",
    "LU",
    "MT",
    "NL",
    "PT",
    "SK",
    "SI",
    "ES",
  ];

  // Country code to country name mapping (subset for common countries)
  const countryNames: Record<string, string> = {
    AT: "Austria",
    BE: "Belgium",
    HR: "Croatia",
    CY: "Cyprus",
    EE: "Estonia",
    FI: "Finland",
    FR: "France",
    DE: "Germany",
    GR: "Greece",
    IE: "Ireland",
    IT: "Italy",
    LV: "Latvia",
    LT: "Lithuania",
    LU: "Luxembourg",
    MT: "Malta",
    NL: "Netherlands",
    PT: "Portugal",
    SK: "Slovakia",
    SI: "Slovenia",
    ES: "Spain",
    US: "United States",
    GB: "United Kingdom",
    CA: "Canada",
    AU: "Australia",
    JP: "Japan",
    CN: "China",
    IN: "India",
    BR: "Brazil",
    MX: "Mexico",
    AE: "United Arab Emirates",
    SA: "Saudi Arabia",
    ZA: "South Africa",
    OM: "Oman",
  };

  const detectCurrency = async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      // Try Cloudflare trace endpoint (most reliable, no rate limits)
      const response = await fetch("https://www.cloudflare.com/cdn-cgi/trace");

      if (!response.ok) {
        throw new Error("Failed to fetch location data");
      }

      const text = await response.text();

      // Parse the trace data
      const data: Record<string, string> = {};
      text.split("\n").forEach((line) => {
        const [key, value] = line.split("=");
        if (key && value) {
          data[key] = value;
        }
      });

      const countryCode = data.loc || "US";

      setLocation({
        country: countryNames[countryCode] || countryCode,
        countryCode: countryCode,
      });

      // Check if country is in eurozone
      if (euroCountries.includes(countryCode)) {
        setCurrency("EUR");
      } else {
        setCurrency("USD");
      }

      setLoading(false);
    } catch (err) {
      // Fallback: Try to use browser's locale/timezone as hint
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const locale = navigator.language;

        // Simple heuristic based on timezone and locale
        const isEurozone =
          locale.startsWith("de") ||
          locale.startsWith("fr") ||
          locale.startsWith("es") ||
          locale.startsWith("it") ||
          locale.startsWith("nl") ||
          locale.startsWith("pt") ||
          timezone.includes("Europe");

        setCurrency(isEurozone ? "EUR" : "USD");
        setLocation({
          country: "Detected from browser",
          countryCode: locale.split("-")[1] || "US",
        });
        setError("Using browser locale as fallback");
      } catch {
        setError("Failed to detect location, defaulting to USD");
        setCurrency("USD");
      }

      setLoading(false);
    }
  };

  useEffect(() => {
    detectCurrency();
  }, []);

  const formatPrice = (amount: number): string => {
    if (currency === "EUR") {
      return `€${amount.toFixed(2)}`;
    }
    return `$${amount.toFixed(2)}`;
  };

  return {
    currency,
    location,
    loading,
    error,
    formatPrice,
    refetch: detectCurrency,
  };
};
